import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  getAdminSettings,
  getPizasSettings,
  updateAdminSettings,
  updatePizasSettings,
  sendDailyRemindersNow,
  uploadWhatsappTreeGreetingImage,
  resolveMediaUrl,
  superAdminResetAllAppointments,
  SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE,
  superAdminResetAllOrders,
  SUPER_RESET_ORDERS_CONFIRM_PHRASE,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { toast } from 'sonner';
import { Image as ImageIcon, Phone, Save, Settings2, Sun, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === '1';

const WA_AI_API_KEY_MASK = '********';

const initialSettings = {
  wa_booking_whatsapp_enabled: false,
  wa_booking_whatsapp_template: '',
  wa_confirmation_whatsapp_enabled: true,
  wa_confirmation_whatsapp_template: '',
  appointment_reminder_enabled: true,
  appointment_reminder_minutes_before: 120,
  appointment_reminder_check_interval_sec: 60,
  public_base_url: 'http://localhost',
  appointment_reminder_template: '',
  appointment_daily_reminder_template: '',
  appointment_daily_reminder_time: '',
  wa_tree_greeting_image_url: '',
  wa_tree_greeting_text: '',
  wa_ai_enabled: false,
  wa_ai_base_url: 'https://generativelanguage.googleapis.com/v1beta/openai',
  wa_ai_model: 'gemini-2.5-flash',
  wa_ai_business_context: '',
  wa_ai_api_key: '',
  voice_ai_enabled: true,
  voice_ai_greeting: '',
  voice_ai_transfer_number: '',
};

export default function AdminSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);
  const [forceDailyResend, setForceDailyResend] = useState(false);
  const [uploadingWaGreeting, setUploadingWaGreeting] = useState(false);
  const [resetAppointmentsBusy, setResetAppointmentsBusy] = useState(false);
  const [resetAppointmentsPhrase, setResetAppointmentsPhrase] = useState('');
  const [resetOrdersBusy, setResetOrdersBusy] = useState(false);
  const [resetOrdersPhrase, setResetOrdersPhrase] = useState('');
  const [sidebarMode, setSidebarMode] = useState('auto');
  const [savingSidebar, setSavingSidebar] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getAdminSettings();
        setSettings({ ...initialSettings, ...data });
      } catch {
        toast.error('No se pudo cargar la configuraci?n');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
    if (PIZAS_MODE) {
      getPizasSettings()
        .then((d) => {
          const m = String(d?.admin_sidebar_mode || 'auto').toLowerCase();
          setSidebarMode(m === 'fixed' ? 'fixed' : 'auto');
        })
        .catch(() => {});
    }
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const saveSidebarMode = async () => {
    setSavingSidebar(true);
    try {
      const current = await getPizasSettings();
      await updatePizasSettings({
        ...current,
        admin_sidebar_mode: sidebarMode,
      });
      window.dispatchEvent(
        new CustomEvent('pizas-ui-config-changed', { detail: { admin_sidebar_mode: sidebarMode } }),
      );
      toast.success('Menú lateral actualizado');
    } catch {
      toast.error('No se pudo guardar el menú lateral');
    } finally {
      setSavingSidebar(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        appointment_reminder_minutes_before: Number(settings.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(settings.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (settings.appointment_daily_reminder_time || '').trim(),
      };
      const saved = await updateAdminSettings(payload);
      setSettings(saved);
      if (PIZAS_MODE) {
        const pizas = await getPizasSettings();
        await updatePizasSettings({ ...pizas, admin_sidebar_mode: sidebarMode });
        window.dispatchEvent(
          new CustomEvent('pizas-ui-config-changed', { detail: { admin_sidebar_mode: sidebarMode } }),
        );
      }
      toast.success('Configuraci?n guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo guardar la configuraci?n');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUploadWhatsappGreetingImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingWaGreeting(true);
    try {
      const { image_url: imageUrl } = await uploadWhatsappTreeGreetingImage(file);
      const next = { ...settings, wa_tree_greeting_image_url: imageUrl };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen de saludo WhatsApp guardada');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo subir la imagen');
    } finally {
      setUploadingWaGreeting(false);
      e.target.value = '';
    }
  };

  const handleClearWhatsappGreetingImage = async () => {
    setUploadingWaGreeting(true);
    try {
      const next = { ...settings, wa_tree_greeting_image_url: '' };
      const saved = await updateAdminSettings({
        ...next,
        appointment_reminder_minutes_before: Number(next.appointment_reminder_minutes_before),
        appointment_reminder_check_interval_sec: Number(next.appointment_reminder_check_interval_sec),
        appointment_daily_reminder_time: (next.appointment_daily_reminder_time || '').trim(),
      });
      setSettings(saved);
      toast.success('Imagen de saludo quitada');
    } catch {
      toast.error('No se pudo quitar la imagen');
    } finally {
      setUploadingWaGreeting(false);
    }
  };

  const handleSendDailyReminders = async () => {
    setSendingDaily(true);
    try {
      const r = await sendDailyRemindersNow({ force: forceDailyResend });
      toast.success(
        `Recordatorios matutinos: ${r.sent} enviados` +
          (r.failed ? `, ${r.failed} fallidos` : '') +
          (r.skipped_no_phone ? `, ${r.skipped_no_phone} sin teléfono` : '')
      );
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el envío');
      }
    } finally {
      setSendingDaily(false);
    }
  };

  const handleResetAllOrders = async () => {
    if (resetOrdersPhrase.trim() !== SUPER_RESET_ORDERS_CONFIRM_PHRASE) {
      toast.error('Escribe la frase de confirmación exacta.');
      return;
    }
    if (
      !window.confirm(
        'Se eliminarán TODOS los pedidos y checkouts de pago en MongoDB. El siguiente pedido será #0001. ¿Continuar?'
      )
    ) {
      return;
    }
    setResetOrdersBusy(true);
    try {
      const r = await superAdminResetAllOrders(resetOrdersPhrase);
      setResetOrdersPhrase('');
      toast.success(
        `Eliminados ${r.orders_deleted ?? 0} pedidos. Contador reiniciado (próximo: ${r.next_order_number ?? '0001'}).`
      );
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo ejecutar el reset');
    } finally {
      setResetOrdersBusy(false);
    }
  };

  const handleResetAllAppointments = async () => {
    if (resetAppointmentsPhrase.trim() !== SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE) {
      toast.error('Escribe la frase de confirmación exacta.');
      return;
    }
    if (
      !window.confirm(
        'Se eliminarán todas las citas en la base de datos (incluidas historial de estados, recordatorios asociados en Mongo). No se borran eventos en Google Calendar. ¿Continuar?'
      )
    ) {
      return;
    }
    setResetAppointmentsBusy(true);
    try {
      const r = await superAdminResetAllAppointments(resetAppointmentsPhrase);
      setResetAppointmentsPhrase('');
      toast.success(`Eliminadas ${r.appointments_deleted ?? 0} citas.`);
      if (r.google_calendar_note) {
        toast.info(String(r.google_calendar_note), { duration: 12000 });
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('No se pudo ejecutar el reset');
      }
    } finally {
      setResetAppointmentsBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando configuraci?n...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">
          {PIZAS_MODE
            ? 'Menú lateral, URL del sitio, WhatsApp y opciones del sistema'
            : 'Configura variables din?micas del sistema'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {PIZAS_MODE ? (
          <Card className="border-2 border-primary/30 bg-primary/5" data-testid="admin-sidebar-mode-settings">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-primary" />
                Menú lateral del panel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 max-w-lg">
              <p className="text-sm text-muted-foreground">
                En escritorio: el menú de navegación puede ocultarse y mostrarse al pasar el mouse por el borde
                izquierdo, o permanecer siempre visible. Aplica a todos los usuarios del panel (admin, caja, cocina).
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer rounded-lg border p-3 hover:bg-muted/40">
                  <input
                    type="radio"
                    name="admin_sidebar_mode"
                    value="auto"
                    checked={sidebarMode === 'auto'}
                    onChange={() => setSidebarMode('auto')}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium block">Automático (oculto)</span>
                    <span className="text-sm text-muted-foreground">
                      Se oculta; pasa el mouse por el borde izquierdo o usa el botón ☰ para abrirlo.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer rounded-lg border p-3 hover:bg-muted/40">
                  <input
                    type="radio"
                    name="admin_sidebar_mode"
                    value="fixed"
                    checked={sidebarMode === 'fixed'}
                    onChange={() => setSidebarMode('fixed')}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium block">Fijo (siempre visible)</span>
                    <span className="text-sm text-muted-foreground">
                      El menú queda abierto en escritorio; más espacio útil en pantallas grandes.
                    </span>
                  </span>
                </label>
              </div>
              <Button type="button" onClick={saveSidebarMode} disabled={savingSidebar}>
                {savingSidebar ? 'Guardando…' : 'Guardar menú lateral'}
              </Button>
            </CardContent>
          </Card>
        ) : null}
        {PIZAS_MODE ? (
          <Card className="border-2 border-primary/30 bg-primary/5">
            <CardHeader>
              <CardTitle>URL pública del sitio (enlaces de pago)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Los links de pago por WhatsApp usan esta URL + <code className="text-xs">/pagar/ID</code>.
                En local suele ser <strong>http://127.0.0.1:3080</strong>; en producción tu dominio HTTPS.
              </p>
              <div>
                <Label htmlFor="public-base-url-pizas">URL pública base</Label>
                <Input
                  id="public-base-url-pizas"
                  value={settings.public_base_url}
                  onChange={(e) => handleChange('public_base_url', e.target.value)}
                  className="h-11 mt-1"
                  placeholder="https://tudominio.com"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Ejemplo de link: {settings.public_base_url || 'https://tudominio.com'}/pagar/abc123
              </p>
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                Debe ser tu dominio web (ej. https://tudominio.com), no un enlace link.mercadopago.com.mx.
              </p>
            </CardContent>
          </Card>
        ) : null}
        {PIZAS_MODE ? (
          <p className="text-sm text-muted-foreground rounded-md border p-3 bg-muted/30">
            La configuración de <strong>Mercado Pago</strong> está en el menú{' '}
            <strong>Config tienda</strong> (Access Token, webhook y activación).
          </p>
        ) : null}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-primary" />
              Plantilla de saludo WhatsApp (menú del árbol)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {PIZAS_MODE ? (
                <>
                  Primer mensaje del bot al escribir <em>menu</em> o iniciar chat. Use{' '}
                  <strong>*asteriscos*</strong> para negritas. Puede usar{' '}
                  <code className="text-xs">{'{store_name}'}</code> (nombre en{' '}
                  <Link to="/admin/config-tienda" className="text-primary underline">
                    Config tienda
                  </Link>
                  ). Vacío = saludo por defecto del servidor. La imagen opcional se envía antes del texto.
                </>
              ) : (
                <>
                  Primer mensaje que ve el paciente en WhatsApp al escribir <em>menu</em> o al iniciar el chat
                  (recepción virtual). Use <strong>*asteriscos*</strong> para negritas. Vacío = texto por defecto del
                  servidor. La imagen opcional se envía antes de este texto. El worker del puente debe alcanzar la{' '}
                  <strong>URL pública base</strong> (tarjeta de recordatorios abajo).
                </>
              )}
            </p>
            <div className="space-y-2">
              <Label htmlFor="wa_tree_greeting_text">Plantilla de bienvenida (texto)</Label>
              <Textarea
                id="wa_tree_greeting_text"
                rows={5}
                value={settings.wa_tree_greeting_text ?? ''}
                onChange={(e) => handleChange('wa_tree_greeting_text', e.target.value)}
                placeholder={
                  PIZAS_MODE
                    ? '¡Hola! Bienvenido a *{store_name}* 🍕'
                    : 'Somos la recepción virtual de *PodoClinicAM* y con gusto le orientamos en temas de *podología*.'
                }
                className="mt-1 font-mono text-sm"
              />
            </div>
            {settings.wa_tree_greeting_image_url ? (
              <div className="rounded-lg border border-border overflow-hidden max-w-xs bg-muted/30">
                <img
                  src={resolveMediaUrl(settings.wa_tree_greeting_image_url)}
                  alt="Vista previa saludo WhatsApp"
                  className="w-full h-auto object-contain max-h-40"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin imagen configurada.</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="max-w-sm cursor-pointer"
                disabled={uploadingWaGreeting}
                onChange={handleUploadWhatsappGreetingImage}
              />
              {settings.wa_tree_greeting_image_url ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingWaGreeting}
                  onClick={handleClearWhatsappGreetingImage}
                >
                  Quitar imagen
                </Button>
              ) : null}
            </div>
            {PIZAS_MODE ? (
              <Button onClick={handleSave} className="w-full rounded-full gap-2" disabled={saving}>
                <Save className="w-4 h-4" />
                {saving ? 'Guardando…' : 'Guardar ajustes'}
              </Button>
            ) : null}
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              Recepción con IA (WhatsApp)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Activa una recepción conversacional con IA para tomar pedidos por WhatsApp.
              Cuando está <strong>apagada</strong>, el bot usa el menú automático de siempre.
              La IA solo responde si está encendida <strong>y</strong> hay API key. Si falla,
              vuelve al árbol automático.
            </p>
            <label className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg cursor-pointer">
              <Checkbox
                checked={Boolean(settings.wa_ai_enabled)}
                onCheckedChange={(checked) => handleChange('wa_ai_enabled', Boolean(checked))}
              />
              <span>Encender la recepción con IA en WhatsApp</span>
            </label>
            <div className="space-y-2">
              <Label htmlFor="wa_ai_api_key">API key</Label>
              <Input
                id="wa_ai_api_key"
                type="password"
                autoComplete="new-password"
                value={settings.wa_ai_api_key ?? ''}
                onChange={(e) => handleChange('wa_ai_api_key', e.target.value)}
                placeholder={
                  settings.wa_ai_api_key === WA_AI_API_KEY_MASK
                    ? 'API key guardada (déjala así para no cambiarla)'
                    : 'Pega aquí tu API key'
                }
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Por seguridad la key no se muestra completa. Déjala igual para conservarla; escribe una nueva
                solo si quieres reemplazarla.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="wa_ai_model">Modelo</Label>
                <Input
                  id="wa_ai_model"
                  value={settings.wa_ai_model ?? ''}
                  onChange={(e) => handleChange('wa_ai_model', e.target.value)}
                  placeholder="gemini-2.5-flash"
                  className="mt-1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wa_ai_base_url">URL base (compatible OpenAI / Gemini)</Label>
                <Input
                  id="wa_ai_base_url"
                  value={settings.wa_ai_base_url ?? ''}
                  onChange={(e) => handleChange('wa_ai_base_url', e.target.value)}
                  placeholder="https://generativelanguage.googleapis.com/v1beta/openai"
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="wa_ai_business_context">Contexto del negocio (instrucciones a la IA)</Label>
              <Textarea
                id="wa_ai_business_context"
                rows={5}
                value={settings.wa_ai_business_context ?? ''}
                onChange={(e) => handleChange('wa_ai_business_context', e.target.value)}
                placeholder="Ej: Somos Pizas. Entregamos en 5 km. Promo 2x1 los martes..."
                className="mt-1"
              />
            </div>
            <Button onClick={handleSave} className="w-full rounded-full gap-2" disabled={saving}>
              <Save className="w-4 h-4" />
              {saving ? 'Guardando…' : 'Guardar ajustes'}
            </Button>
          </CardContent>
        </Card>

        {PIZAS_MODE ? (
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-primary" />
                Contestador telefónico con IA (Twilio)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Contesta las llamadas desviadas a tu número Twilio, resuelve dudas del menú y toma pedidos
                (aparecen en la bandeja con origen <strong>Teléfono</strong>). Usa la misma API key y modelo de la
                recepción con IA de arriba. Requiere que el servidor tenga <code>VOICE_AI_ENABLED=1</code> y las
                credenciales de Twilio (ver Manual de usuario).
              </p>
              <label className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg cursor-pointer">
                <Checkbox
                  checked={Boolean(settings.voice_ai_enabled)}
                  onCheckedChange={(checked) => handleChange('voice_ai_enabled', Boolean(checked))}
                />
                <span>Contestar llamadas con IA (si se apaga, las llamadas se pasan al número de abajo o se toma recado)</span>
              </label>
              <div className="space-y-2">
                <Label htmlFor="voice_ai_greeting">Saludo al contestar</Label>
                <Textarea
                  id="voice_ai_greeting"
                  rows={3}
                  value={settings.voice_ai_greeting ?? ''}
                  onChange={(e) => handleChange('voice_ai_greeting', e.target.value)}
                  placeholder="Hola, gracias por llamar a {store_name}. Soy el asistente virtual y puedo tomar tu pedido. ¿Qué se te antoja hoy?"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground">
                  Vacío = saludo por defecto. Puedes usar <code>{'{store_name}'}</code> para el nombre de la tienda.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="voice_ai_transfer_number">Número para transferir a una persona</Label>
                <Input
                  id="voice_ai_transfer_number"
                  value={settings.voice_ai_transfer_number ?? ''}
                  onChange={(e) => handleChange('voice_ai_transfer_number', e.target.value)}
                  placeholder="+525512345678"
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Si el cliente pide hablar con alguien o la IA falla, se le comunica a este número (formato +52 y 10
                  dígitos; no uses el mismo número que desvía a Twilio). Vacío = se toma un recado de voz. También
                  recibe por WhatsApp los recados.
                </p>
              </div>
              <Button onClick={handleSave} className="w-full rounded-full gap-2" disabled={saving}>
                <Save className="w-4 h-4" />
                {saving ? 'Guardando…' : 'Guardar ajustes'}
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {PIZAS_MODE && user && isSuperAdmin(user.role) ? (
          <Card className="border border-red-200 bg-red-50/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-900">
                <Trash2 className="w-5 h-5 shrink-0" />
                Super administrador — Inicio de operación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-red-900/90 leading-relaxed">
                Borra <strong>todos los pedidos</strong> en MongoDB y reinicia el contador a <strong>#0001</strong>.
                También elimina checkouts de Mercado Pago pendientes. No borra menú, usuarios ni configuración.
                Úsalo solo al poner en marcha el sistema o en pruebas.
              </p>
              <div>
                <Label htmlFor="reset-orders-phrase" className="text-red-900">
                  Escribe la frase exacta para habilitar el botón
                </Label>
                <Input
                  id="reset-orders-phrase"
                  value={resetOrdersPhrase}
                  onChange={(e) => setResetOrdersPhrase(e.target.value)}
                  className="mt-1 font-mono text-sm bg-white border-red-200"
                  placeholder={SUPER_RESET_ORDERS_CONFIRM_PHRASE}
                  autoComplete="off"
                />
              </div>
              <Button
                type="button"
                variant="destructive"
                className="rounded-full gap-2"
                disabled={
                  resetOrdersBusy || resetOrdersPhrase.trim() !== SUPER_RESET_ORDERS_CONFIRM_PHRASE
                }
                onClick={handleResetAllOrders}
              >
                <Trash2 className="w-4 h-4" />
                {resetOrdersBusy ? 'Eliminando…' : 'Borrar todos los pedidos y reiniciar contador'}
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {!PIZAS_MODE ? (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              Recordatorios de citas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <label className="flex items-center gap-3 p-3 bg-sage-50 rounded-lg cursor-pointer">
              <Checkbox
                checked={settings.appointment_reminder_enabled}
                onCheckedChange={(checked) => handleChange('appointment_reminder_enabled', Boolean(checked))}
              />
              <span>Activar recordatorios autom?ticos por WhatsApp</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reminder-minutes">Tiempo de anticipaci?n (minutos)</Label>
                <Input
                  id="reminder-minutes"
                  type="number"
                  min="1"
                  max="10080"
                  value={settings.appointment_reminder_minutes_before}
                  onChange={(e) => handleChange('appointment_reminder_minutes_before', e.target.value)}
                  className="h-11 mt-1"
                />
              </div>
              <div>
                <Label htmlFor="reminder-interval">Frecuencia de revisi?n (segundos)</Label>
                <Input
                  id="reminder-interval"
                  type="number"
                  min="15"
                  max="3600"
                  value={settings.appointment_reminder_check_interval_sec}
                  onChange={(e) => handleChange('appointment_reminder_check_interval_sec', e.target.value)}
                  className="h-11 mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="daily-reminder-time">Hora del recordatorio del d?a (citas de hoy)</Label>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Input
                  id="daily-reminder-time"
                  type="time"
                  value={settings.appointment_daily_reminder_time || ''}
                  onChange={(e) => handleChange('appointment_daily_reminder_time', e.target.value)}
                  className="h-11 max-w-[220px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-11 rounded-full"
                  onClick={() => handleChange('appointment_daily_reminder_time', '')}
                >
                  Quitar hora
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                A esa hora (zona del calendario de la clínica, p. ej. America/Mexico_City) se envía un WhatsApp
                de recordatorio a cada paciente con cita programada para la fecha actual. Deja el campo vacío
                para no usar este envío masivo. Es independiente del recordatorio por anticipación (minutos
                antes de la hora de la cita).
              </p>
            </div>

            <div>
              <Label htmlFor="daily-reminder-template">Plantilla recordatorio matutino (citas de hoy)</Label>
              <Textarea
                id="daily-reminder-template"
                rows={6}
                value={settings.appointment_daily_reminder_template}
                onChange={(e) => handleChange('appointment_daily_reminder_template', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se usa solo para el envío del día (hora programada o botón de super admin). Mismos placeholders
                que el recordatorio por anticipación.
              </p>
            </div>

            {user && isSuperAdmin(user.role) ? (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <p className="text-sm font-medium">Super administrador</p>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={forceDailyResend}
                    onCheckedChange={(c) => setForceDailyResend(Boolean(c))}
                  />
                  <span>Reenviar a todos los de hoy aunque ya se haya enviado el matutino</span>
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  className="rounded-full gap-2"
                  disabled={sendingDaily}
                  onClick={handleSendDailyReminders}
                >
                  <Sun className="w-4 h-4" />
                  {sendingDaily ? 'Enviando…' : 'Enviar recordatorios matutinos ahora'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Usa la plantilla matutina y la fecha de hoy en la zona del calendario. No afecta el recordatorio
                  por anticipación (minutos antes).
                </p>

                <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 space-y-3">
                  <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
                    <Trash2 className="w-4 h-4 shrink-0" />
                    Reset masivo de citas
                  </p>
                  <p className="text-xs text-red-900/90 leading-relaxed">
                    Borra <strong>todas</strong> las citas guardadas en MongoDB (pendientes, confirmadas, canceladas,
                    completadas, etc.). El historial de citas vive en la misma colección: quedará vacía. No elimina
                    productos, reseñas, usuarios ni la configuración del panel. Los eventos en{' '}
                    <strong>Google Calendar</strong> no se borran automáticamente.
                  </p>
                  <div>
                    <Label htmlFor="reset-appointments-phrase" className="text-red-900">
                      Escribe la frase exacta para habilitar el botón
                    </Label>
                    <Input
                      id="reset-appointments-phrase"
                      value={resetAppointmentsPhrase}
                      onChange={(e) => setResetAppointmentsPhrase(e.target.value)}
                      className="mt-1 font-mono text-sm bg-white border-red-200"
                      placeholder={SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    className="rounded-full gap-2"
                    disabled={
                      resetAppointmentsBusy ||
                      resetAppointmentsPhrase.trim() !== SUPER_RESET_APPOINTMENTS_CONFIRM_PHRASE
                    }
                    onClick={handleResetAllAppointments}
                  >
                    <Trash2 className="w-4 h-4" />
                    {resetAppointmentsBusy ? 'Eliminando…' : 'Eliminar todas las citas'}
                  </Button>
                </div>
              </div>
            ) : null}

            {!PIZAS_MODE ? (
              <div>
                <Label htmlFor="public-base-url">URL pública base</Label>
                <Input
                  id="public-base-url"
                  value={settings.public_base_url}
                  onChange={(e) => handleChange('public_base_url', e.target.value)}
                  className="h-11 mt-1"
                  placeholder="https://tudominio.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Se usa para construir links de confirmación/cancelación/reagendado.
                </p>
              </div>
            ) : null}

            <div>
              <Label htmlFor="reminder-template">Plantilla recordatorio por anticipación (antes de la cita)</Label>
              <Textarea
                id="reminder-template"
                rows={7}
                value={settings.appointment_reminder_template}
                onChange={(e) => handleChange('appointment_reminder_template', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se envía cuando falten los minutos configurados para la hora de la cita. Placeholders:{' '}
                {'{patient_name}'}, {'{date}'}, {'{time}'}, {'{confirm_url}'}, {'{cancel_url}'},{' '}
                {'{reschedule_url}'}.
                {'{manage_url}'} ya no se usa en recordatorios (se ignora si aparece en plantillas antiguas).
              </p>
            </div>
          </CardContent>
        </Card>
        ) : null}

        {!PIZAS_MODE ? (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              WhatsApp al confirmar la cita
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              El paciente solo recibe WhatsApp cuando pulsas <strong>Confirmar</strong> en Citas (las
              solicitudes desde el sitio quedan pendientes hasta entonces).
            </p>
            <label className="flex items-center gap-3 p-3 bg-sage-50 rounded-lg cursor-pointer">
              <Checkbox
                checked={settings.wa_confirmation_whatsapp_enabled}
                onCheckedChange={(checked) =>
                  handleChange('wa_confirmation_whatsapp_enabled', Boolean(checked))
                }
              />
              <span>Enviar WhatsApp de confirmaci?n cuando el administrador confirma la cita</span>
            </label>

            <div>
              <Label htmlFor="confirmation-template">Plantilla de confirmaci?n</Label>
              <Textarea
                id="confirmation-template"
                rows={6}
                value={settings.wa_confirmation_whatsapp_template}
                onChange={(e) => handleChange('wa_confirmation_whatsapp_template', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Placeholders: {'{patient_name}'}, {'{date}'}, {'{time}'}, {'{reason}'}, {'{reason_line}'}.
              </p>
            </div>

            <Button onClick={handleSave} className="w-full rounded-full gap-2" disabled={saving}>
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar ajustes'}
            </Button>
          </CardContent>
        </Card>
        ) : null}
      </div>
    </div>
  );
}
